import type { DenunciaCategoria } from '../denuncia/categoria.js';
import type { Gravidade, RotaComite } from '../denuncia/triagem.js';
import type { Voto } from '../denuncia/parecer.js';
import type { DecisaoKind } from '../denuncia/decisao.js';
import type { OitivaStatus } from '../denuncia/oitiva.js';
import type { SubRole } from '../auth/user.js';

export const PHASE1_EVENT_TYPES = [
  'denuncia_aberta',
  'status_consultado',
  'info_complementar_adicionada',
  // Divergência 3 Fatia C — resposta do denunciante ao comitê pelo canal de
  // mensagens (rota pública /api/status, atrás do firewall de anonimato). Payload
  // ANÔNIMO: só caseId + createdWeek (semana), NUNCA userId/IP/UA — o denunciante
  // é anônimo. Conteúdo é cifrado sob a DEK (server nunca vê texto claro).
  'mensagem_denunciante_recebida',
  'attachment_uploaded',
  'cofre_used', // Phase 1 single-key break-glass (D-45 / COF-04)
] as const;

export type Phase1AuditEventType = (typeof PHASE1_EVENT_TYPES)[number];

export const PHASE2_EVENT_TYPES = [
  // Auth (Phase 2)
  'login_success',
  'login_failed',
  'login_a11y_fallback_used',
  'session_revoked',
  'account_locked',
  // Comitê workflow
  'triagem_classificada',
  'membros_atribuidos',
  'instrucao_evidencia_anexada',
  'oitiva_registrada',
  'oitiva_dispensada',
  // Fatia 5 (Trilha 3) — anexação da ata/justificativa cifrada à oitiva, mudando
  // o status pendente_anexo → anexada (PATCH /oitiva/:id/attach-ata). Fecha a
  // cadeia que ADR-0008 §"Audit forense" antecipou: o tempo entre oitiva_registrada
  // (pendente) e oitiva_ata_anexada é dado forense. Payload reusa oitivaStatus.
  'oitiva_ata_anexada',
  // Fase 2 não-DEK passo 3 (ADR-0008): fechamento explícito da fase Oitivas
  // (transição Oitivas → AvaliacaoEmCurso via POST /close-oitivas). Sem audit
  // ficaria sem rastro forense da decisão de avançar.
  'oitivas_encerradas',
  'parecer_emitido',
  'conflito_declarado',
  'decisao_emitida',
  'prazo_recursal_aberto',
  'notificacao_denunciante_enviada',
  'notificacao_denunciado_enviada',
  'caso_encerrado',
  // P1 (B-05) — disposição de triagem (IT-CMP-002 §5). Content-free: caseId +
  // userId + createdWeek + motivo/destino (enum), NUNCA a justificativa cifrada.
  // Fonte definitiva do indicador P2 "arquivadas na triagem" (sobrevive ao Encerramento).
  'denuncia_arquivada_triagem',
  'denuncia_encaminhada_triagem',
  // P2 indicador 8 — marca content-free de que a triagem classificou o relato como
  // retaliação. Só caseId + userId + createdWeek. Fonte do indicador (sobrevive ao
  // Encerramento, ao contrário do state.triagem).
  'triagem_relato_retaliacao',
  // Admin
  'usuario_criado',
  'usuario_desativado',
  // ADR-0021 — a volta do ciclo de vida. Conta nunca é apagada, então a trilha
  // precisa registrar a reativação com o mesmo peso da inativação; sem isto o
  // log mostra o desligamento e não mostra o retorno.
  'usuario_ativado',
  /**
   * 🔴 O GESTOR ÚNICO FOI DESIGNADO (ADR-0022 + decisão do operador 2026-08-17).
   *
   * `userId` = a conta criada. **SEM `actorId`** — e a ausência é informação.
   *
   * 🔴 CORREÇÃO (2026-08-24): este comentário afirmava "`actorId` = o `admin_ti` que a
   * criou", e isso deixou de ser verdade no mesmo dia. A cerimônia saiu de dentro do
   * `adminApp` porque a resposta devolve `firstAccessToken` + `otpAuthUri`, e quem tem os
   * dois define a senha e ASSUME a conta — o `admin_ti` que criava o gestor podia se
   * tornar o gestor. A rota passou a ser gateada por posse de segredo, sem sessão.
   *
   * Consequência para a trilha, dita sem atenuação: **não há ator identificado**. O ato
   * foi autorizado por posse de `ADMIN_MASTER_SETUP_SECRET`, e preencher `actorId` com
   * qualquer valor sintético publicaria autoria falsa numa projeção que a tela apresenta
   * como "Quem fez". A ausência significa exatamente "criado pelo caminho do segredo de
   * setup"; reconstruir a autoria depende de controle FORA do canal.
   *
   * Fica registrado em vez de apagado porque comentário que garante invariante MORTO é
   * pior que comentário nenhum: lê-se como auditoria já feita.
   *
   * POR QUE UM EVENTO PRÓPRIO, e não só o `usuario_criado` que a criação já emite:
   * este é o ato que concentra mais poder no sistema — a conta lê o teor de todos os
   * casos que enxerga, conduz sozinha (ADR-0022), alcança o cofre e ocupa uma das duas
   * cadeiras de autorização de acesso. `usuario_criado` é indistinguível entre isso e
   * um analista de RH, e o `roles` NÃO vai no payload (a projeção é allow-list) — então
   * sem este marco a trilha não responde "quando o gestor único passou a existir, e por
   * ordem de quem", que é a primeira pergunta de qualquer auditoria do ADR-0022.
   *
   * Existe no MÁXIMO um ativo por vez (índice `uniq_admin_master_ativo`, migration 023),
   * então uma sequência de dois destes sem `usuario_desativado` no meio é sinal de que a
   * migration não está aplicada — o marco serve como testemunha disso.
   */
  'admin_master_criado',
  'caso_atribuido',
  // IA mock
  'ia_analista_parecer',
  'ia_juiz_sintese',
  'ia_auditora_block',
  // Demo 2026-05-18 — Banner always-on IA-Auditora. Emitted em todo poll do
  // /auditoria-status (verde/amarelo/vermelho). Severity em auditoraSeverity;
  // motivo em auditoraReason (apenas quando severity !== 'ok'). Backstop forense
  // de cada decisão visual do banner exibido ao SESMT.
  'ia_auditora_validacao',
  // Phase 5 — Recurso
  'recurso_interposto',
  'recurso_parecer_emitido',
  'recurso_decidido',
  'notificacao_recurso_enviada',
  // Phase 6 — Cofre
  'cofre_acesso_solicitado',
  'cofre_assinatura_registrada',
  'cofre_acesso_concedido',
  'cofre_acesso_revogado_24h',
  'cofre_share_distribuido',
  // Plan 06-03 D-154 race resolution — sign attempt em request já Revogado24h
  // ou Concedido (cron tick @14:00 vs sign @14:00:01). Enriquece evidência forense.
  'cofre_assinatura_rejeitada',
  // Phase 7 — Hardening (legal-export Plan 07-01 D-162)
  // Meta-auditável: cada invocação de /api/legal-export/audit-log emite este evento
  // ANTES de retornar o bundle assinado. Bundle exporta o audit log inteiro
  // (com filtro per-case / date-range / full), então o export aparece no próximo
  // export. Auditor cruza requestId + solicitanteUlid + bundleHash para rastrear
  // toda emissão judicial. NÃO contém conteúdo do bundle, NÃO PII denunciante.
  'legal_export_executado',
  // Phase 7 — Hardening (retention Plan 07-03 D-173 LGP-04)
  // Tombstone do retention purge: caseId opaco + purgeType + dekWiped + attachmentCount.
  // NÃO contém ciphertext, NÃO PII denunciante. Preservado indefinidamente para
  // hash chain integrity + prova de existência forense (Lei 12.846 prescrição 5y).
  // Actor é system_cron (sem userId — D-147 paridade cofre-revoke).
  'dado_purgado',
  // Phase 7 — Hardening (legal hold toggle Plan 07-03 Task 4 D-172)
  // Emitted quando admin_ti seta/remove flag legalHold via POST /api/admin/cases/:id/legal-hold.
  // Requer judicial token 2-of-3 cofre signing (paralelo a Plan 07-01 legal-export).
  // Payload contém caseId + userId + legalHoldValue (true|false). NÃO contém conteúdo.
  'legal_hold_alterado',
  // Phase 2.1 — Biometria audit-only (D-186 + D-188 — AUTH-02)
  // Forensic-only events: membro_autenticado_biometria registra presença física
  // do membro no momento do login (NUNCA inclui embedding/descriptor/frame —
  // apenas hash SHA-256 hex 64 chars). biometria_rate_limit_excedido bloqueia
  // abuso de captura (max 5/min/userId — face-api é caro).
  'membro_autenticado_biometria',
  'biometria_rate_limit_excedido',
  // Demo 2026-05-15 — facial match comparativo (feature flag dark BIOMETRIC_MATCH_ENABLED).
  // Forense indelével: enrollment one-shot + cada tentativa de match (success/fail).
  // NUNCA inclui embedding/template ciphertext — apenas matchDistance numérico + matchOk bool +
  // templateChecksum truncado (16 chars sha256 do ciphertext) para dedup audit. Audit captura
  // foto-impressa que passa o match porque face-api não tem liveness — backstop forense.
  'membro_enrollment_facial',
  'membro_match_sucesso',
  'membro_match_falha',
  'membro_match_admin_override',
  // ADR-0009 — Cadastro do template de liveness (API externa) pelo admin TI.
  // Etapa "cadastro": admin captura a foto do membro → /extract-template → template
  // cifrado at-rest. enrolled = primeiro cadastro; reenrolled = regravação (recuperação
  // (c) por mudança de aparência — D6, autoridade do admin, auditada). Payload carrega
  // SÓ userId (membro alvo) + templateChecksum (16 hex do ciphertext) + capturedAt —
  // NUNCA a foto, NUNCA o template cru. Produção bloqueada por governança (gates DPIA §5).
  'membro_liveness_enrolled',
  'membro_liveness_reenrolled',
  // ADR-0022 (cerimônia atômica, 2026-08-24) — AUTO-cadastro do template no próprio
  // primeiro acesso, pela pessoa, dentro do único ato que também define a senha.
  //
  // Por que é evento SEPARADO de `membro_liveness_enrolled`: ali existe `actorId` (o
  // admin_ti que operou a câmera com o membro presente) e aqui NÃO EXISTE — ator e
  // sujeito são a mesma pessoa, autorizada por posse do link. Reaproveitar o evento
  // anterior faria a trilha afirmar uma testemunha que não houve. Quem auditar precisa
  // poder separar "um admin viu esta pessoa" de "esta pessoa cadastrou o próprio rosto".
  //
  // Payload: SÓ userId + templateChecksum (16 hex do ciphertext) + capturedAt. NUNCA a
  // foto, NUNCA o template cru — paridade com os dois eventos acima.
  'membro_liveness_autocadastrado',
  // ADR-0009 Peça 4 — verificação de liveness no LOGIN do comitê (2ª ponta).
  // comite_liveness_attempt: emitido a cada laudo REAL (liberar/negar) — NUNCA em
  // degraded / invalid_capture / upstream. comite_liveness_locked: no 3º negar (lock
  // POR-USUÁRIO, durável até override da TI). Payload carrega SÓ userId + outcome +
  // failureCount + reason coarse — NUNCA confidence/variacao/template/vídeo (zero
  // dado biométrico; paridade D-187 / membro_match_* sanitizado).
  'comite_liveness_attempt',
  'comite_liveness_locked',
  // ADR-0009 Peça 5 — modo degradado (API de liveness fora do ar). Emitido SÓ quando
  // o operador (TI) ligou LIVENESS_DEGRADED_ALLOW e o membro entrou SEM laudo durante
  // uma queda. SEMPRE com degraded:true — distinto de um liberar por laudo positivo.
  // NÃO conta tentativa nem mexe no contador de lockout. Payload: userId + degraded.
  'comite_liveness_degraded',
  // ADR-0009 Peça 7 — recuperação por OVERRIDE DA TI. admin_ti reseta o lockout
  // biométrico de um membro (Peça 4), auditado e time-boxed. NÃO é bypass de acesso:
  // o membro AINDA precisa passar no liveness; falhar 3x de novo re-trava (exige novo
  // override). Payload: actorId (admin) + userId (membro) + overrideReason (por quê) +
  // overrideExpiresAt (janela de recuperação). NUNCA dado biométrico.
  'comite_liveness_override',
  // Phase 2.2 — Admin onboarding pleno (ADM-01, ADM-02, ADM-03)
  // Eventos forenses ligados a composição mínima do comitê (NR-04) + nomeação
  // PDF wrapped 2x (admin + escrow per OD-1). Payload sanitizado — NUNCA
  // contém filename original (apenas filenameHash SHA-256), NUNCA contém
  // wrappedDek bytes (apenas presença true/false), NUNCA contém PII do
  // membro afetado (apenas userId opaco). Paridade Phase 1 D-23.
  'membro_adicionado',
  'membro_removido',
  'composicao_alterada',
  'membro_externo_obrigatorio_solicitado',
  'atribuicao_bloqueada_externo_ausente',
  /** Tentativa de abrir instrução com a triagem colegiada INCOMPLETA (decisão do
   *  operador, 2026-08-13: todos os membros ativos classificam antes). Irmão do
   *  `atribuicao_bloqueada_externo_ausente` acima — mesma rota, mesma forma: o
   *  bloqueio é registrado, não só devolvido, senão "o caso não avançou" fica sem
   *  explicação na trilha. Content-free: caseId + userId + semana/hora. */
  'atribuicao_bloqueada_triagem_incompleta',
  /** Voto de UM membro para encerrar a fase de oitivas (decisão do operador,
   *  2026-08-13: só encerra depois que todos os membros do roster votarem).
   *
   *  É o registro POR MEMBRO que `oitivas_encerradas` — evento único, do ato
   *  consumado — não conseguia representar. A contagem é derivada da trilha, como a
   *  da triagem colegiada: append-only é a forma natural de um voto, e assim não
   *  precisou de migration nem de store novo.
   *
   *  Content-free: caseId + userId + semana/hora. NÃO carrega juízo de mérito — é
   *  "concordo em encerrar a fase", não voto de procedência. Por isso fica FORA do
   *  voto cego (ADR-0014), que protege `voto`/`parecer`. */
  'oitiva_fechamento_votado',
  /** Membro do comitê BAIXOU um anexo da denúncia (operador, 2026-08-13).
   *
   *  Ato de leitura de conteúdo por pessoa identificada — o mesmo peso forense do
   *  acesso ao teor. Content-free: caseId + userId + contentHash (que já é público no
   *  record) + semana/hora. NUNCA o nome do arquivo nem os bytes.
   *
   *  Existe porque "quem abriu qual documento, e quando" é a pergunta que um vazamento
   *  faz — e sem este append a resposta não existiria em lugar nenhum. */
  'anexo_baixado_comite',
  /** Plano de Investigação (FP-CMP-010) gerado e COMPARTILHADO com o comitê
   *  (ADR-0023). Content-free: caseId + userId + semana/hora. NUNCA o conteúdo do
   *  plano, que traz nomes e papéis de pessoas e vive cifrado sob a DEK.
   *
   *  Existe porque o plano passou a ser documento do caso, e "quem elaborou o plano, e
   *  quando" é dado de rito — o `IT-CMP-002` §Fase 4 atribui a elaboração ao comitê. */
  'plano_investigacao_gerado',
  'triagem_bloqueada_composicao',
  'nomeacao_pdf_uploadada',
  'nomeacao_pdf_excedeu_limite',
  // Sprint 3.1 — Aceite formal da Política de Não-Retaliação LGP-02.
  // Emitido por POST /api/comite/policy-accept (e gêmeos admin/recurso/cofre).
  // Payload contém apenas userId + policyVersion + acceptedAt (NÃO email,
  // NÃO IP, NÃO UA — anonymity firewall paridade defensiva em rota auth).
  // Permite ao auditor reconstruir "todos os usuários aceitaram LGP-02 v0.1
  // antes do primeiro uso" via varredura do hash chain (cumpre art. 23
  // Lei 14.457/2022 — evidência de programa de integridade efetivo).
  'policy_accepted',
  // F2 Parte A — troca de senha autenticada (POST /api/comite/change-password).
  // Usuário logado redefine a própria senha provando os 2 fatores atuais
  // (authHash atual + TOTP). Payload: SÓ userId — NUNCA senha/hash (nem antiga
  // nem nova), NUNCA TOTP. Backstop forense de toda rotação de credencial.
  'senha_alterada',
  // F2 Parte C — reset pesado de credenciais (dois admins). Membro que perdeu
  // senha E TOTP. NÃO é self-service: exige dois admin_ti distintos.
  //   _solicitado: admin A abre o pedido (actorId=A, userId=subject, requestId, resetReason).
  //   _executado:  admin B (distinto) aprova → regenera credenciais + emite first-access
  //                (actorId=B, userId=subject, requestId, coApprovedBy=A). Os DOIS actorIds
  //                ficam rastreáveis. NUNCA senha/TOTP/biometria.
  //   _cancelado:  pedido aberto por engano, cancelado antes da 2ª aprovação.
  'credential_reset_solicitado',
  'credential_reset_executado',
  'credential_reset_cancelado',
  // ADR-0010 Fatia 1 (Trilha 3) — enrollment do keypair RSA-OAEP-2048 do membro do
  // comitê. Emitido por POST /api/comite/enroll-keypair quando o membro grava o par
  // pela primeira vez (first-writer-wins). Payload: SÓ userId + createdWeek — NUNCA
  // o material de chave (pública/privada-embrulhada/nonce). Backstop forense de quando
  // cada membro passou a ter par assimétrico (cross-ref Fatia 2/4).
  'comite_keypair_enrolled',
  // Fase 2 prazos soft (2026-06-26) — prorrogação +30 da Investigação. Ação
  // MANUAL de um membro do comitê (Lei 14.457: rastro de quem prorrogou e por
  // quê). NÃO muta state/record (princípio soft: muda só o prazo exibido). É a
  // ÚNICA fonte de "foi prorrogado" — derivado deste evento nas projeções.
  // Payload: caseId + userId + createdWeek + prorrogacaoMotivo (cleartext
  // OPCIONAL, justificativa administrativa — NÃO PII de denunciante).
  'investigacao_prorrogada',
  // Comitê & IA (ADR-0014) — eventos do rito COLEGIADO (único; sem flag). Payload
  // sanitizado: caseId + userId/actorId opacos + metadados (voto/decisaoKind/
  // reaberturasCount). NUNCA conteúdo de denúncia, NUNCA PII de denunciante.
  'membro_impedido_v2', // recusa explícita (R3.2-bis) — DISTINTA da atestação /conflito.
  'quorum_fechado_v2', // fechar_quorum — quórum colegiado roster fechado.
  'ia_juiz_proposta_v2', // LEGADO/inerte (ADR-0014 — IA-Juiz removida; não é mais emitido).
  'caso_reaberto_v2', // comite_reabre — o comitê devolveu o caso (com reaberturasCount).
  'retorno_definido_v2', // comite_define_retorno — comitê escolheu nova instrução/votação.
  'substituto_designado_v2', // admin_ti trocou um humano impedido pelo substituto do pool.
  'esgotamento_instrucao_v2', // decisão de mérito forçada no teto de 2 reaberturas (R4.5).
  // P5 (members-rewrap / ADR "Fatia 4 destravada") — um leitor legítimo propagou o
  // acesso ao caso re-embrulhando a DEK (RSA) para membros do comitê que entraram
  // DEPOIS do submit (self-heal client-side). Payload content-free: caseId + userId
  // (quem propagou) + createdWeek + rewrapAlvos (ids OPACOS de membro do comitê —
  // identidade de membro é não-anônima por design; NUNCA PII de denunciante, NUNCA
  // material de chave/DEK/ciphertext). Backstop forense de quem passou a poder ler.
  'membros_rewrap_v2',
  /**
   * GESTÃO UNIPESSOAL (2026-08-13) — o `admin_master` ASSUMIU a condução do caso.
   *
   * Marco de abertura do modo: a partir dele, cada ato do gestor no caso carrega
   * `modoUnipessoal: true`. Existe como evento PRÓPRIO, e não só como flag nos atos,
   * porque o momento em que a condução deixou de ser colegiada é a informação que um
   * auditor procura primeiro — e derivá-la de "o primeiro ato marcado" faria a
   * resposta depender de varrer a trilha inteira.
   *
   * Payload: caseId + userId (quem assumiu) + createdWeek. Content-free.
   */
  /** Triagem de conflito respondeu: o denunciado é (ou não) membro do comitê. */
  'triagem_conflito_concluida',
  /** A IA não respondeu após N tentativas — caso liberado ao comitê COM AVISO. */
  'triagem_conflito_liberada_sem_analise',
  'caso_assumido_unipessoal',
  /** Gestor devolveu o caso ao colegiado ao propagar acesso (operador 14/08). Também é
   *  a FRONTEIRA de uma delegação: zera os condutores antes de nomear os novos. */
  'conducao_unipessoal_encerrada',
  /** CONDUÇÃO DELEGADA (operador 17/08) — um evento por membro escolhido. `userId` é o
   *  DESIGNADO, não quem designou; quem designou fica no `conducao_unipessoal_encerrada`
   *  imediatamente anterior, que é o par que delimita o ato na trilha. */
  'conducao_delegada',
  /** MEMBRO EXTERNO (operador 18/08) — rota `com_membro_externo`. `userId` é quem
   *  INDICOU; `externoId` é o indicado (membro do Comitê de Recurso). */
  'membro_externo_indicado',
  /** Voto do colegiado elegível na indicação. `userId` = quem votou, `aprovou` = o voto,
   *  `externoId` = o nome votado (separa as rodadas numa trilha append-only). */
  'membro_externo_voto',
  /** Indicação retirada por quem indicou, antes de fechar a unanimidade. */
  'membro_externo_retirado',
  /** SOLICITAÇÃO DE ACESSO (operador 19/08) — membro que entrou depois do submit pede
   *  autorização para ler. `userId` é quem PEDE. */
  'acesso_solicitado',
  /** Acesso concedido: `userId` é quem RECEBEU, `actorId` é quem concedeu. O wrap em si
   *  nasce no browser do concedente (servidor cego, ADR-0002) — este marco registra o ATO,
   *  que é o que o self-heal não tinha. */
  'acesso_concedido',
  /** AUTORIZAÇÃO DA SOLICITAÇÃO (operador 23/08) — `userId` é o SOLICITANTE (sujeito),
   *  `actorId` é quem autorizou, `requestId` amarra a rodada.
   *
   *  🔴 AUTORIZAR NÃO É CONCEDER, e os dois marcos existem separados por isso. Este
   *  registra o DIREITO de ler, decidido por `admin_ti` + `admin_master`; o
   *  `acesso_concedido` registra a chave efetivamente entregue, que só nasce no browser
   *  de quem tem a DEK (ADR-0002). Fundir os dois faria a trilha afirmar que alguém lê o
   *  caso no instante em que ninguém ainda embrulhou a chave para ele. */
  'acesso_autorizado',
  /** Solicitação NEGADA: `userId` é o solicitante, `actorId` quem negou, `requestId` a
   *  rodada. Encerra a rodada — pedir de novo abre outra, com `requestId` novo. */
  'acesso_negado',
  /**
   * CHAVE ENTREGUE PELO ESCROW (operador, 2026-08-23). `userId` = quem recebeu,
   * `actorId` = o admin que executou.
   *
   * 🔴 EVENTO PRÓPRIO, e não um `acesso_concedido` com `degraded: true`.
   *
   * O ataque adversarial de 23/08 mediu que `degraded` e `actorId` são DESCARTADOS pelas
   * duas projeções de leitura da trilha — ou seja, numa tela de auditoria a concessão via
   * escrow ficaria idêntica a um re-wrap comum de membro. Um `eventType` distinto sobrevive
   * a qualquer projeção que filtre por tipo, e responde por grep a pergunta que importa:
   * "quantas vezes a custódia única foi aberta, e para quem?".
   *
   * É o análogo do `cofre_acesso_concedido` para esta via — que NÃO passa pelo quórum
   * 2-de-3 do ADR-0015, e por isso precisa de rastro pelo menos tão bom.
   */
  'acesso_concedido_via_escrow',
] as const;

export type Phase2AuditEventType = (typeof PHASE2_EVENT_TYPES)[number];

export type AuditEventType = Phase1AuditEventType | Phase2AuditEventType;

export interface Phase1AuditPayload {
  eventType: Phase1AuditEventType;
  caseId: string;
  createdWeek: string;
  /**
   * Data e hora do ato — DECISÃO DO OPERADOR (2026-08-13), tomada depois de eu
   * apontar o custo de anonimato e ele reafirmar com escopo explícito ("aberto em,
   * todas as ações do histórico em data e hora").
   *
   * 🔴 ISTO REVERTE O ADR-0019 §7.1. Até aqui este tipo NÃO declarava o campo, e a
   * ausência era a defesa: era fisicamente impossível carimbar hora num ato de
   * denunciante, e o `tsc` reprovava quem tentasse (TS2375). Mesma técnica do
   * `AnalistaContext`, que não declara `humanVerdicts`.
   *
   * ⚠️ O QUE ISTO REABRE, para quem precisar decidir de novo: o eixo de correlação
   * temporal do denunciante. Com hora cheia em `status_consultado` e
   * `denuncia_aberta`, quem tiver a trilha pode cruzar com ponto, crachá, catraca,
   * câmera e log de acesso e estreitar "1.300 colaboradores" para "quem estava no
   * prédio às 13:38:13" — é o cenário que o LT-01 fechou em 30/07.
   *
   * O que NÃO foi reaberto: `caseId` segue 130 bits aleatórios sem timestamp, os
   * ulids do lado denunciante seguem opacos, o `audit_log.ulid` segue
   * não-monotônico e o `CreationDate` do PDF segue constante. Quatro das seis peças
   * do LT-01 continuam de pé — caiu a granularidade, não os artefatos.
   */
  createdAtEpochMs?: number;
  // Optional fields per event type (D-36):
  contentHash?: string; // attachment_uploaded only — SHA-256 hex of ciphertext
  sizeBucket?: '<1MB' | '1-5MB' | '5-25MB'; // attachment_uploaded only
  categoria?: DenunciaCategoria; // denuncia_aberta only
  motivoHash?: string; // cofre_used only
}

export interface Phase2AuditPayload {
  eventType: Phase2AuditEventType;
  /** caseId só presente em eventos do rito (não em login). */
  caseId?: string;
  createdWeek: string;
  /** userId em eventos auth + admin + parecer (NÃO presente em eventos públicos). */
  userId?: string;
  /** ADR-0009 — ator que executou a ação, quando distinto do `userId` (sujeito).
   *  Ex.: em membro_liveness_enrolled/_reenrolled, userId = membro alvo (sujeito do
   *  template) e actorId = admin_ti que fez o cadastro. Essencial forense: re-enroll é
   *  ação tipo-override (saber QUAL admin recadastrou um rosto). Opcional non-breaking;
   *  ausente em eventos onde ator e sujeito coincidem. NUNCA contém PII (apenas userId opaco). */
  actorId?: string;
  /** ADR-0019 §7 — epoch-ms do ato, presente SOMENTE quando há ator identificado
   *  (`userId` ou `actorId`). Carimbado no `append()` dos adaptadores via
   *  `carimbarHora` (audit/temporalidade.ts), não sítio a sítio.
   *
   *  ⚠️ AUSENTE, nunca zerado, quando não há ator (§7.5): um `0` plausível
   *  tornaria "sem hora por desenho" indistinguível de "hora perdida por bug".
   *  Em particular `status_consultado` — a consulta do denunciante anônimo —
   *  NÃO carrega este campo; carregá-lo reabriria o LT-01 pelo eixo das visitas
   *  ao canal, e a trilha é exportada ao juiz.
   *
   *  NÃO substitui `createdWeek`: a semana continua sendo o que vai para o
   *  legal-export e para as superfícies mais expostas (§7.5). */
  createdAtEpochMs?: number;
  /** SHA-256 hex do ciphertext do anexo (chave no storage). Presente em
   *  `anexo_baixado_comite`. NAO e sensivel: ja esta no record e deriva do
   *  ciphertext — nao revela conteudo nem nome do arquivo. */
  contentHash?: string;
  /** Eventos auth — login_failed, account_locked etc — sem leak de email cleartext. */
  emailHash?: string;
  /** ADR-0009 Peça 4 — verificação de liveness. Presentes em comite_liveness_attempt/
   *  _locked. NUNCA dado biométrico (sem confidence/variacao/template/vídeo). */
  livenessOutcome?: 'liberar' | 'negar';
  livenessFailureCount?: number;
  livenessReason?: 'sem_movimento' | 'desafio_incorreto' | 'rosto_nao_corresponde';
  /** ADR-0009 Peça 5 — true em comite_liveness_degraded: o membro entrou por liberação
   *  de operador (API fora do ar), NÃO por laudo. Marca forense do bypass sancionado. */
  degraded?: boolean;
  /** ADR-0009 Peça 7 — epoch ms do fim da janela de recuperação do override da TI.
   *  Presente em comite_liveness_override (reusa overrideReason p/ o "por quê"). */
  overrideExpiresAt?: number;
  /** LT-52 — recibo PERMANENTE do export judicial: quantas entradas saíram no bundle.
   *  Antes a contagem existia só no header HTTP `X-Entry-Count`, que não sobrevive a
   *  nada: um export VAZIO ficava indistinguível de um legítimo na própria trilha que
   *  deveria testemunhá-lo. Número puro — sem PII, e fora do `PAYLOAD_DENY_PATTERN`
   *  do bundle de propósito (é justamente o que se quer poder auditar).
   *  O handler já escrevia este campo desde `a6e738c`; a declaração faltava. */
  entryCount?: number;
  /** LT-52 — por qual caminho a trilha foi lida, para distinguir export íntegro de
   *  export por caminho velho caso volte a existir mais de um. Union declarada aqui
   *  (e não importada de `apps/api`) porque `@aions/core` é puro: adapters não podem
   *  ser importados para cá. Espelha `MetodoLeitura` de `legal-export-bundle.ts` —
   *  se um valor novo nascer lá, tem de nascer aqui junto. */
  metodoLeitura?: 'getEntriesAsync';
  /** Triagem. */
  gravidade?: Gravidade;
  rota?: RotaComite;
  /** Triagem — `true` quando o caso JÁ tinha uma classificação anterior, i.e. este
   *  `triagem_classificada` é uma RECLASSIFICAÇÃO (não a primeira). Marca o ato
   *  separadamente na trilha forense; ausente/false = classificação inicial.
   *  Campo opcional non-breaking (entries antigas parsam sem ele). */
  reclassificacao?: boolean;
  /** Fase 2 não-DEK passo 3 (ADR-0008) — status da oitiva/dispensa registrada.
   *  Presente em `oitiva_registrada`/`oitiva_dispensada` ('pendente_anexo') e em
   *  `oitiva_ata_anexada` ('anexada', Fatia 5 — conteúdo cifrado anexado). */
  oitivaStatus?: OitivaStatus;
  /** Parecer. */
  voto?: Voto;
  /** Decisão. */
  decisaoKind?: DecisaoKind;
  /** P1 (B-05) — disposição de triagem (content-free enum). Presente em
   *  denuncia_arquivada_triagem / denuncia_encaminhada_triagem. */
  motivoArquivamento?: import('../denuncia/encerramento.js').MotivoArquivamento;
  destinoEncaminhamento?: import('../denuncia/encerramento.js').DestinoEncaminhamento;
  /**
   * Phase 5 — voto do painel de recurso (REC-02 + REC-04). Populated em eventos
   * `recurso_parecer_emitido` e `recurso_decidido`. Campo opcional non-breaking:
   * entries Phase 2 antigas (sem este campo) continuam parsando OK; entries
   * novas (com este campo) também parsam. JSON serde tolera ausência.
   */
  recursoVoto?: 'manter' | 'reformar' | 'reformar_parcial';
  /** IA-Auditora block reason. */
  auditoraReason?: string;
  /** Demo 2026-05-18 — severidade do banner IA-Auditora (`ia_auditora_validacao`).
   *  'ok' = rito em conformidade; 'warning' = pendência recomendada; 'block' = transição vetada. */
  auditoraSeverity?: 'ok' | 'warning' | 'block';
  // Phase 6 cofre — campos opcionais non-breaking (entries antigas continuam parsing).
  /** Identifica o pedido de break-glass (rastreabilidade audit + UI cofre/audit-page). */
  requestId?: string;
  /** SHA-256 hex (truncado) do motivo cleartext — preserva privacidade do conteúdo. */
  motivoHash?: string;
  /** Quem assinou o pedido — paridade `cofre_holder` (Gabriel/Dr. João/Jéssica) ou `admin_ti`. */
  signerRole?: 'cofre_holder' | 'admin_ti';
  /** Timestamp epoch-ms da janela `expiresAt` (24h). Auditor cruza com timestamps. */
  expiresAt?: number;
  /** denunciaId cifrado no cofre — separado do `caseId` global porque /reconstruct
   *  precisa de rastreio dedicado (não é o caso da denúncia em si). */
  cofreDenunciaId?: string;
  // Phase 7 — Hardening (legal-export Plan 07-01 D-162)
  // Campos opcionais non-breaking para `legal_export_executado`. Entries antigas
  // (sem estes campos) continuam parsing OK; entries novas (com estes campos) também.
  /** Escopo do bundle exportado: per-case (judicial), date-range (regulatório),
   *  full (staging-only, proibido em produção — D-161). */
  exportMode?: 'per-case' | 'date-range' | 'full';
  /** Range cobrado pelo bundle. caseId presente em mode 'per-case'; from/to em 'date-range'. */
  exportRange?: { from?: string; to?: string; caseId?: string };
  /** SHA-256 hex de canonical(export.json) — hash do bundle assinado (D-160). */
  bundleHash?: string;
  /** Identificador da chave Ed25519 usada na assinatura (rotação D-157). */
  signingKeyId?: string;
  // Phase 7 — Retention (Plan 07-03 D-173 LGP-04)
  // Campos opcionais non-breaking para `dado_purgado`. Entries antigas (sem estes
  // campos) continuam parsing OK; entries novas (com estes campos) também.
  /** Tipo de artefato purgado (D-170 tiers): denúncia (5y locked), attachment (1-5y configurável),
   *  cofre_request (1y após Concedido/Revogado24h). */
  purgeType?: 'denuncia' | 'attachment' | 'cofre_request';
  /** Flag de evidência: DEK overwrite com zeros + fill(0) executado. Cron actor é system_cron. */
  dekWiped?: boolean;
  /** Quantidade de attachments R2 deletados junto com a denúncia (∈ [0, N]). */
  attachmentCount?: number;
  // Phase 7 — Legal hold toggle (Plan 07-03 Task 4 D-172)
  /** Valor setado pelo POST /api/admin/cases/:id/legal-hold. true bloqueia purge mesmo após 5y;
   *  false reabilita purge. Auditor cruza com timestamps + signerUlids do token judicial. */
  legalHoldValue?: boolean;
  // Phase 2.1 — Biometria audit (D-186 + AUTH-02)
  // Campos opcionais non-breaking. Entries antigas (sem estes campos) continuam
  // parsing OK. NUNCA inclui embedding/descriptor/frame — apenas hash (D-187 + DPIA §7).
  /** Phase 2.1 — SHA-256 hex (64 chars) do embedding facial. NUNCA contém o
   *  embedding/descriptor/frame. Regex validado server-side `^[0-9a-f]{64}$`. */
  biometricHash?: string;
  /** Phase 2.1 — epoch-ms quando a captura ocorreu (separado de createdWeek por ser
   *  evento de presença forense, não de denúncia — ANON-04 não aplica a comitê). */
  capturedAt?: number;
  /** Phase 2.1 — razão do skip quando o membro não capturou biometria (Phase 2.3
   *  A11-02 wireup). Sem o skip flow ativo em Phase 2.1, este campo permanece
   *  undefined em todos os eventos `membro_autenticado_biometria` desta fase. */
  cameraSkipReason?: 'no_camera' | 'permission_denied' | 'a11y_fallback';

  // Demo 2026-05-15 — Facial match (BIOMETRIC_MATCH_ENABLED dark flag).
  /** Distância euclidiana entre embedding capturado e template enrolled.
   *  Float32Array(128) face-api descriptor. Threshold 0.45 (match) / >0.45 (fail).
   *  Sempre presente em `membro_match_sucesso` e `membro_match_falha`. */
  matchDistance?: number;
  /** Resultado binário do match. Redundante com matchDistance vs threshold mas
   *  facilita query forense ("últimas N falhas"). */
  matchOk?: boolean;
  /** SHA-256 truncado (16 hex chars) do ciphertext do template enrolled — dedup
   *  audit + correlação enrollment ↔ match. NUNCA o template em si. */
  templateChecksum?: string;
  /** Motivo do override admin em `membro_match_admin_override` (ADR-0005) E em
   *  `comite_liveness_override` (ADR-0009 Peça 7) — texto livre, audit-only. NUNCA
   *  contém PII do membro afetado, apenas razão administrativa do admin_ti. */
  overrideReason?: string;

  // Phase 2.2 — Admin onboarding pleno (D-205, D-212, D-217)
  // Campos opcionais non-breaking. Entries antigas (sem estes campos) parsing OK.

  /** Phase 2.2 D-212 — sub-role do membro afetado (membro_adicionado / composicao_alterada). */
  subRole?: SubRole;
  /** Phase 2.2 D-212 — sub-role removida (apenas membro_removido). Separado de
   *  subRole para diferenciar add (snapshot pós-mudança) de remove (snapshot pré-mudança). */
  subRoleRemovido?: SubRole;
  /** Phase 2.2 D-205 — SHA-256 hex (64 chars) do filename original do PDF.
   *  NUNCA o filename real (pode conter PII). Audit dedup + forensics. */
  filenameHash?: string;
  /** Phase 2.2 D-204 — bucket de tamanho do PDF (não exact bytes). Paridade Phase 1 attachment. */
  sizeBucket?: '<1MB' | '1-5MB' | '5-25MB';
  /** Phase 2.2 D-211 — roles ausentes quando triagem bloqueada por composição. */
  missing?: ReadonlyArray<'rh' | 'juridico' | 'sesmt'>;
  /** Phase 2.2 D-217 — tipo de vendor externo (apenas em membro_adicionado quando subRole='externo'). */
  externoVendorType?: 'auditoria_independente' | 'juridico_externo' | 'outro';
  /** Phase 2.2 D-212 — snapshot da composição pós-mudança (apenas composicao_alterada).
   *  Estrutura literal — NUNCA inclui userIds individuais (apenas counts). */
  composicaoSnapshot?: {
    readonly rh: number;
    readonly juridico: number;
    readonly sesmt: number;
    readonly externos: number;
    readonly total: number;
    readonly operacional: boolean;
  };

  // F2 Parte C — reset pesado de credenciais (dois admins).
  /** Motivo administrativo do reset (texto do admin A). NÃO é conteúdo de denúncia.
   *  Presente em credential_reset_solicitado. */
  resetReason?: string;
  /** O OUTRO aprovador (admin A) em credential_reset_executado, onde actorId=B.
   *  Permite ter os dois actorIds num só evento (rastreabilidade da dupla-aprovação). */
  coApprovedBy?: string;

  // Sprint 3.1 — Aceite LGP-02 (policy_accepted)
  /** Versão da política aceita (ex: 'LGP-02-0.1'). Casado com LGP_02_VERSION em core/auth/policy.ts. */
  policyVersion?: string;
  /** Epoch-ms do aceite. Redundante com audit entry timestamp mas explícito para query forense. */
  policyAcceptedAt?: number;

  // Fase 2 prazos soft — prorrogação +30 da Investigação (investigacao_prorrogada)
  /** Justificativa administrativa OPCIONAL da prorrogação (cleartext, capada).
   *  NÃO é conteúdo de denúncia nem PII de denunciante — é o "por quê" do membro
   *  do comitê (Lei 14.457). Ausente quando o membro não informou motivo. */
  prorrogacaoMotivo?: string;

  // Phase 8 (Comitê & IA v2) — campos opcionais non-breaking.
  /** Nº de reaberturas já ocorridas (caso_reaberto_v2 / retorno_definido_v2). Teto 2 (R4.5). */
  reaberturasCount?: number;
  /** Destino escolhido pelo comitê na reabertura (retorno_definido_v2). */
  retornoTarget?: 'instrucao' | 'votacao';
  /** Substituto por conflito (substituto_designado_v2) — userIds OPACOS de membro
   *  do comitê (NÃO PII de denunciante; identidade de membro é não-anônima por design). */
  substitutoDeMemberId?: string;
  substitutoParaMemberId?: string;
  /** P5 (membros_rewrap_v2) — ids OPACOS de membro do comitê que passaram a poder
   *  ler o caso (alvos do re-wrap). Identidade de MEMBRO é não-anônima por design
   *  (user.ts) — ZERO PII de denunciante, NUNCA material de chave/DEK/ciphertext.
   *  Descartado pelas projeções de leitura (allow-list por-chave, audit-projection.ts). */
  rewrapAlvos?: ReadonlyArray<string>;
  /** MEMBRO EXTERNO (2026-08-18) — id OPACO do membro do Comitê de Recurso indicado.
   *  Identidade de MEMBRO é não-anônima por design (user.ts); ZERO PII de denunciante. */
  externoId?: string;
  /** Voto na indicação do membro externo. Booleano estrito — ausente não é reprovação,
   *  é voto que não houve, e o fold trata os dois de formas diferentes. */
  aprovou?: boolean;
  /**
   * GESTÃO UNIPESSOAL (2026-08-13) — este ato foi praticado por `admin_master` num
   * caso que ele ASSUMIU, sem colegialidade.
   *
   * Segue o precedente do `degraded` (login que passou com um fator a menos): ato
   * praticado com uma garantia a menos **nunca é silencioso**. Entra no
   * `payloadCanonical`, portanto dentro de `sha256(prevHash + payload)`, do export
   * judicial e do relatório de indicadores — não é metadado de UI.
   *
   * AUSENTE, nunca `false`, quando o ato é colegiado: presença da chave É o
   * significado, e um `false` gravado em todo ato colegiado inflaria a trilha e
   * tornaria a busca por "houve condução unipessoal?" uma varredura de valor em vez
   * de existência. Mesma regra do §7.5 para a hora.
   */
  modoUnipessoal?: true;
}

export type AuditPayload = Phase1AuditPayload | Phase2AuditPayload;
